import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';

import { IconComponent } from '@shared/icon';
import { type IconName } from '@shared/icon';

import { chipCva, chipTrailingIconCva } from './chip.cva';
import { type ChipAppearance } from './chip.types';

@Component({
  selector: 'ml-chip',
  imports: [IconComponent],
  templateUrl: './chip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipComponent {
  readonly label = input.required<string>();
  readonly type = input<ChipAppearance>('default');
  readonly leadingIcon = input<IconName | null>();

  readonly clicked = output<Event>();
  // Mutable by directives (DismissableDirective, SelectableDirective) via inject
  readonly isSelected = signal(false);
  readonly isDismissable = signal(false);
  readonly trailingIcon = signal<IconName | null>(null);

  readonly trailingIconSize = signal<'sm' | 'xs'>('sm');

  private readonly usesFilledAppearance = computed(
    () => !this.isSelected() && this.type() === 'filled',
  );

  protected readonly classes = computed(() => {
    const selected = this.isSelected();
    const dismissable = this.isDismissable();
    const filledAppearance = this.usesFilledAppearance();

    return chipCva({
      appearance: filledAppearance ? 'filled' : 'default',
      selected,
      dismissable,
    });
  });

  protected readonly trailingIconClasses = computed(() => {
    const selected = this.isSelected();
    const dismissable = this.isDismissable();
    const filledAppearance = this.usesFilledAppearance();

    return chipTrailingIconCva({
      appearance: filledAppearance ? 'filled' : 'outlined',
      selected,
      dismissable,
    });
  });
}
